import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Check, Shield, Settings2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { isInGdprRegion, OPEN_COOKIE_SETTINGS_EVENT } from '../utils/gdpr';
interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}
const STORAGE_KEY = 'ahg.cookie_consent.v1';
// Default record written silently for non-GDPR visitors so analytics scripts
// can read a deterministic consent state without ever bothering the user.
const NON_GDPR_DEFAULT: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false
};
export function CookieConsent() {
  const { lang, isRTL } = useLanguage();
  const isAr = lang === 'ar';
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });
  // Decide whether the banner should auto-show. Only EU/EEA/UK/CH visitors
  // see it on first visit. Everyone else gets a silent "necessary only"
  // record written so analytics gating still works.
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return; // already decided — never re-prompt
    if (isInGdprRegion()) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    // Non-GDPR visitor: silently set a default record. Do NOT show banner.
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(NON_GDPR_DEFAULT));
    } catch {

      /* private mode — ignore */}
  }, []);
  // Listen for "open cookie settings" requests from anywhere (e.g. footer
  // link). This lets users in any region open the preferences manually.
  useEffect(() => {
    const handler = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) setPreferences(JSON.parse(stored));
      } catch {

        /* ignore parse errors */}
      setShowCustomize(true);
      setIsVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
  }, []);
  const saveConsent = (prefs: ConsentPreferences) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setIsVisible(false);
    // Here you would typically trigger GTM or other analytics scripts
    // based on the accepted preferences
  };
  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    });
  };
  const handleRejectAll = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    });
  };
  const handleSavePreferences = () => {
    saveConsent(preferences);
  };
  const togglePreference = (key: keyof ConsentPreferences) => {
    if (key === 'necessary') return; // Cannot toggle necessary
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const content = {
    title: isAr ? 'نحن نقدر خصوصيتك' : 'We value your privacy',
    message: isAr ?
    'نستخدم ملفات تعريف الارتباط لتحسين تجربة التصفح الخاصة بك، وتقديم إعلانات أو محتوى مخصص، وتحليل حركة المرور لدينا. بالنقر على "قبول الكل"، فإنك توافق على استخدامنا لملفات تعريف الارتباط.' :
    'We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
    acceptAll: isAr ? 'قبول الكل' : 'Accept All',
    rejectAll: isAr ? 'رفض الكل' : 'Reject All',
    customize: isAr ? 'تخصيص' : 'Customize',
    save: isAr ? 'حفظ التفضيلات' : 'Save Preferences',
    categories: {
      necessary: {
        title: isAr ? 'ضرورية جداً' : 'Strictly Necessary',
        desc: isAr ?
        'هذه الملفات ضرورية لعمل الموقع ولا يمكن إيقافها.' :
        'These cookies are necessary for the website to function and cannot be switched off.'
      },
      analytics: {
        title: isAr ? 'التحليلات' : 'Analytics',
        desc: isAr ?
        'تساعدنا في فهم كيفية تفاعل الزوار مع الموقع.' :
        'Help us understand how visitors interact with the website.'
      },
      marketing: {
        title: isAr ? 'التسويق' : 'Marketing',
        desc: isAr ?
        'تستخدم لتتبع الزوار عبر المواقع لتقديم إعلانات ذات صلة.' :
        'Used to track visitors across websites to display relevant ads.'
      },
      preferences: {
        title: isAr ? 'التفضيلات' : 'Preferences',
        desc: isAr ?
        'تمكن الموقع من تذكر المعلومات التي تغير طريقة تصرفه أو مظهره.' :
        'Enable the website to remember information that changes the way it behaves or looks.'
      }
    }
  };
  return (
    <AnimatePresence>
      {isVisible &&
      <motion.div
        initial={{
          y: 100,
          opacity: 0
        }}
        animate={{
          y: 0,
          opacity: 1
        }}
        exit={{
          y: 100,
          opacity: 0
        }}
        transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }}
        className="fixed bottom-0 inset-x-0 z-[100] p-4 pointer-events-none">
        
          <div
          className={`max-w-5xl mx-auto bg-white rounded-2xl shadow-[0_-10px_40px_rgba(15,61,92,0.15)] border border-sea-blue/10 overflow-hidden pointer-events-auto ${isAr ? 'font-arabic' : ''}`}>
          
            {/* Main Banner */}
            {!showCustomize ?
          <div className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="flex-grow flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-sand flex items-center justify-center text-terracotta shrink-0">
                    <Cookie size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-sea-blue mb-2">
                      {content.title}
                    </h3>
                    <p className="text-sm text-sea-blue/85 leading-relaxed max-w-3xl">
                      {content.message}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
                  <button
                onClick={() => setShowCustomize(true)}
                className="px-5 py-2.5 text-sm font-medium text-sea-blue hover:bg-sand rounded-xl transition-colors">
                
                    {content.customize}
                  </button>
                  <button
                onClick={handleRejectAll}
                className="px-5 py-2.5 text-sm font-medium text-sea-blue bg-white border border-sea-blue/20 hover:bg-sand rounded-xl transition-colors">
                
                    {content.rejectAll}
                  </button>
                  <button
                onClick={handleAcceptAll}
                className="px-6 py-2.5 text-sm font-medium text-white bg-terracotta-hover hover:brightness-95 rounded-xl shadow-sm transition-colors">
                
                    {content.acceptAll}
                  </button>
                </div>
              </div> /* Customize Modal View */ :

          <div className="flex flex-col max-h-[80vh]">
                <div className="p-6 border-b border-sea-blue/10 flex items-center justify-between bg-sand/30">
                  <div className="flex items-center gap-3">
                    <Settings2 className="text-terracotta" size={24} />
                    <h3 className="text-xl font-bold text-sea-blue">
                      {content.customize}
                    </h3>
                  </div>
                  <button
                onClick={() => setShowCustomize(false)}
                aria-label={isAr ? 'إغلاق' : 'Close'}
                className="p-2 text-sea-blue/50 hover:text-terracotta hover:bg-white rounded-full transition-colors">
                
                    <X size={20} />
                  </button>
                </div>

                <div className="p-6 overflow-y-auto space-y-6">
                  {/* Necessary */}
                  <div className="flex items-start gap-4">
                    <div className="mt-1">
                      <div className="w-10 h-6 bg-sea-blue/30 rounded-full relative opacity-50 cursor-not-allowed">
                        <div className="absolute end-1 top-1 w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-sea-blue">
                          {content.categories.necessary.title}
                        </h4>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-sea-blue/40 bg-sea-blue/5 px-2 py-0.5 rounded">
                          {isAr ? 'دائمًا فعال' : 'Always Active'}
                        </span>
                      </div>
                      <p className="text-sm text-sea-blue/80">
                        {content.categories.necessary.desc}
                      </p>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start gap-4">
                    <button
                  className="mt-1 shrink-0"
                  onClick={() => togglePreference('analytics')}
                  role="switch"
                  aria-checked={preferences.analytics}
                  aria-label={content.categories.analytics.title}>
                  
                      <div
                    className={`w-10 h-6 rounded-full relative transition-colors ${preferences.analytics ? 'bg-terracotta' : 'bg-sea-blue/20'}`}>
                    
                        <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${preferences.analytics ? 'end-1' : 'start-1'}`} />
                    
                      </div>
                    </button>
                    <div>
                      <h4 className="font-bold text-sea-blue mb-1">
                        {content.categories.analytics.title}
                      </h4>
                      <p className="text-sm text-sea-blue/80">
                        {content.categories.analytics.desc}
                      </p>
                    </div>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-start gap-4">
                    <button
                  className="mt-1 shrink-0"
                  onClick={() => togglePreference('marketing')}
                  role="switch"
                  aria-checked={preferences.marketing}
                  aria-label={content.categories.marketing.title}>
                  
                      <div
                    className={`w-10 h-6 rounded-full relative transition-colors ${preferences.marketing ? 'bg-terracotta' : 'bg-sea-blue/20'}`}>
                    
                        <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${preferences.marketing ? 'end-1' : 'start-1'}`} />
                    
                      </div>
                    </button>
                    <div>
                      <h4 className="font-bold text-sea-blue mb-1">
                        {content.categories.marketing.title}
                      </h4>
                      <p className="text-sm text-sea-blue/80">
                        {content.categories.marketing.desc}
                      </p>
                    </div>
                  </div>

                  {/* Preferences */}
                  <div className="flex items-start gap-4">
                    <button
                  className="mt-1 shrink-0"
                  onClick={() => togglePreference('preferences')}
                  role="switch"
                  aria-checked={preferences.preferences}
                  aria-label={content.categories.preferences.title}>
                  
                      <div
                    className={`w-10 h-6 rounded-full relative transition-colors ${preferences.preferences ? 'bg-terracotta' : 'bg-sea-blue/20'}`}>
                    
                        <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${preferences.preferences ? 'end-1' : 'start-1'}`} />
                    
                      </div>
                    </button>
                    <div>
                      <h4 className="font-bold text-sea-blue mb-1">
                        {content.categories.preferences.title}
                      </h4>
                      <p className="text-sm text-sea-blue/80">
                        {content.categories.preferences.desc}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-sea-blue/10 bg-sand/30 flex justify-end gap-3">
                  <button
                onClick={handleRejectAll}
                className="px-5 py-2.5 text-sm font-medium text-sea-blue hover:bg-white rounded-xl transition-colors">
                
                    {content.rejectAll}
                  </button>
                  <button
                onClick={handleSavePreferences}
                className="px-6 py-2.5 text-sm font-medium text-white bg-terracotta-hover hover:brightness-95 rounded-xl shadow-sm transition-colors">
                
                    {content.save}
                  </button>
                </div>
              </div>
          }
          </div>
        </motion.div>
      }
    </AnimatePresence>);

}